/**
 * European Map Configuration
 * Leaflet migration configuration for European-optimized mapping
 */

// European-optimized tile providers
export const EUROPEAN_TILE_PROVIDERS = {
  osm: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap contributors',
    maxZoom: 19,
    subdomains: ['a', 'b', 'c']
  },
  cartodb: {
    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    attribution: '© OpenStreetMap contributors © CARTO',
    maxZoom: 20,
    subdomains: ['a', 'b', 'c', 'd']
  },
  stamen: {
    url: 'https://stamen-tiles-{s}.a.ssl.fastly.net/terrain/{z}/{x}/{y}{r}.png',
    attribution: 'Map tiles by Stamen Design, CC BY 3.0 — Map data © OpenStreetMap contributors',
    maxZoom: 18,
    subdomains: ['a', 'b', 'c', 'd']
  }
};

// Default tile provider
export const DEFAULT_TILE_PROVIDER = 'osm';

// European bounds configuration
export const EUROPE_BOUNDS = [
  [34.5, -40.0],  // Southwest: Southern Spain/Portugal
  [71.0, 60.0]    // Northeast: Northern Scandinavia/Western Russia
];

// European center point optimized for continent view
export const EUROPE_CENTER = [45.5, 5.8]; // Lyon

// Zoom level constraints and thresholds
export const ZOOM_CONFIG = {
  MIN_ZOOM: 4,        // Minimum zoom level (continent view)
  MAX_ZOOM: 12,       // Maximum zoom level (city detail view)
  DEFAULT_ZOOM: 5,    // Default zoom level for initial view
  
  // Zoom thresholds for different display modes
  THRESHOLDS: {
    COUNTRY_ONLY: 6,    // Below this: show only countries
    CITY_VISIBLE: 6,    // Above this: show cities
    CLUSTER_BREAK: 10   // Above this: break clusters into individual markers
  }
};

// Map styling constants
export const MAP_STYLES = {
  // Default map container styles
  container: {
    height: '100%',
    width: '100%',
    borderRadius: '8px',
    overflow: 'hidden'
  },
  
  // Marker cluster styles
  cluster: {
    backgroundColor: '#ff6b35',
    color: '#ffffff',
    borderRadius: '50%',
    border: '2px solid #ffffff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
  },
  
  // Individual marker styles
  marker: {
    color: '#ff6b35',
    fillColor: '#ffffff',
    fillOpacity: 0.8,
    radius: 8,
    weight: 2
  },
  
  // Popup styles
  popup: {
    maxWidth: 300,
    className: 'custom-popup'
  },
  
  // Tooltip styles
  tooltip: {
    className: 'custom-tooltip',
    permanent: false,
    sticky: true
  }
};

// European country bounds for country-specific views
// export const COUNTRY_BOUNDS = {
//   germany: [[47.3, 5.9], [55.1, 15.0]],
//   france: [[41.3, -5.1], [51.1, 9.6]],
//   italy: [[35.5, 6.6], [47.1, 18.5]],
//   spain: [[27.6, -18.2], [43.8, 4.3]],
//   uk: [[49.9, -8.6], [60.8, 1.8]],
//   poland: [[49.0, 14.1], [54.8, 24.1]],
//   netherlands: [[50.8, 3.4], [53.6, 7.2]],
//   sweden: [[55.3, 11.0], [69.1, 24.2]],
//   norway: [[57.9, 4.6], [71.2, 31.3]],
//   finland: [[59.8, 20.5], [70.1, 31.6]]
// };

// Bounds padding configuration
export const BOUNDS_PADDING = {
  padding: [20, 20], // [vertical, horizontal] padding in pixels
  maxZoom: ZOOM_CONFIG.MAX_ZOOM
};

// Animation and interaction settings
export const ANIMATION_CONFIG = {
  // Zoom animation duration
  zoomAnimationDuration: 0.5,
  
  // Pan animation duration
  panAnimationDuration: 0.5,
  
  // Marker animation settings
  markerAnimation: {
    duration: 300,
    easing: 'ease-out'
  },
  
  // Cluster animation settings
  clusterAnimation: {
    animate: true,
    animateAddingMarkers: true,
    disableClusteringAtZoom: ZOOM_CONFIG.THRESHOLDS.CLUSTER_BREAK
  }
};

// Data refresh intervals (in milliseconds)
export const REFRESH_INTERVALS = {
  MAP_DATA: 5 * 60 * 1000,      // 5 minutes
  REAL_TIME_DATA: 30 * 1000,    // 30 seconds
  KPI_DATA: 2 * 60 * 1000       // 2 minutes
};

// Export configuration object for easy importing
export const MAP_CONFIG = {
  tileProviders: EUROPEAN_TILE_PROVIDERS,
  defaultProvider: DEFAULT_TILE_PROVIDER,
  bounds: EUROPE_BOUNDS,
  center: EUROPE_CENTER,
  zoom: ZOOM_CONFIG,
  styles: MAP_STYLES,
  // countryBounds: COUNTRY_BOUNDS,
  boundsOptions: BOUNDS_PADDING,
  animation: ANIMATION_CONFIG,
  refresh: REFRESH_INTERVALS
};

export default MAP_CONFIG;